import { useMediaQuery } from '@mui/material';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { theme } from '../../../../components/theme/theme';
import { Drawer } from 'antd';
import { CloseIcon } from '../../../../components/icons';
import { Permission, Role } from '../../../../interfaces/User';
import * as Yup from 'yup';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import Input from '../../../../components/input/Input';
import TextArea from '../../../../components/input/TextArea';
import { PermissionsView } from './PermissionsView';
import Button from '../../../../components/button/Button';
import { mutateConfig } from '../../../../api/queryBase';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { getErrorMessage } from '../../../../utils/helper';

interface CreateRoleValues {
    name: string;
    description: string;
    permissions: Permission;
}

const CreateRoleModal = ({
    openModal,
    setOpenModal,
    selectedRole,
    setSelectedRole,
    refetch
}: {
    openModal: boolean;
    setOpenModal: Dispatch<SetStateAction<boolean>>;
    selectedRole?: Role;
    setSelectedRole?: Dispatch<SetStateAction<Role | undefined>>;
    refetch?: () => void;
}) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const closeModal = () => {
        setOpenModal(false);
        setSelectedRole && setSelectedRole(undefined);
    };
    const defaultPermissions = {
        logistics: 'view',
        financing: 'view',
        payments: 'view',
        insurance: 'view',
        sales: 'view',
        stocks: 'view',
        tradeDocuments: 'view'
    };

    const initialValues: CreateRoleValues = {
        name: selectedRole?.name || '',
        description: selectedRole?.description || '',
        permissions: {
            ...defaultPermissions,
            ...selectedRole?.permissions
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Role Name is required'),
        description: Yup.string().required('Role description is required')
    });

    const [submitting, setSubmitting] = useState(false);
    const mutation = mutateConfig();

    const { mutate } = mutation({
        url: '/roles/create',
        cacheKeys: 'corporate-roles'
    });

    const { mutate: updateRole } = mutateConfig({ method: 'patch' })({
        url: `/roles/${selectedRole?.id}`,
        cacheKeys: 'corporate-roles'
    });

    const handleSubmit = async (
        values: CreateRoleValues,
        { resetForm }: FormikHelpers<CreateRoleValues>
    ) => {
        mutate(values, {
            onSuccess: () => {
                refetch?.();
                resetForm();
                closeModal();
            },
            onError: (error: AxiosError | undefined | unknown) => {
                toast.error(
                    getErrorMessage(error) ||
                        'An error has occurred. Please try again.'
                );
            },
            onSettled: () => {
                setSubmitting(false);
                refetch?.();
            }
        });
    };

    const handleUpdateRole = (
        values: CreateRoleValues,
        { resetForm }: FormikHelpers<CreateRoleValues>
    ) => {
        setSubmitting(true);
        updateRole(
            { ...values, roleId: selectedRole?.id || '' },
            {
                onSuccess: () => {
                    refetch?.();
                    resetForm();
                    closeModal();
                },
                onError: (error: AxiosError | undefined | unknown) => {
                    toast.error(
                        getErrorMessage(error) ||
                            'An error has occurred. Please try again.'
                    );
                },
                onSettled: () => {
                    setSubmitting(false);
                    refetch?.();
                }
            }
        );
    };

    return (
        <Drawer
            placement={isMobile ? 'bottom' : 'right'}
            closable={false}
            onClose={() => closeModal()}
            open={openModal}
            width={430}
            height={'75vh'}
            className='!bg-white !rounded-t-xl pt-6 lg:pt-0 lg:!rounded-t-none'
        >
            <div className='px-4 lg:w-[400px] mx-auto py-4 lg:max-w-[400px] w-full relative'>
                <div
                    className='absolute right-2 lg:top-10 top-6 cursor-pointer'
                    onClick={closeModal}
                >
                    <CloseIcon />
                </div>
                <h2 className='lg:pt-14 pt-0 mb-2 text-[18px] font-semibold text-ebony'>
                    {selectedRole ? 'Edit Custom Role' : 'Add Custom Role'}
                </h2>
                <h5 className='!text-sm !text-midnightHaze font-light !mb-[24px]'>
                    {selectedRole
                        ? 'Make changes to this custom role.'
                        : 'Create a new custom role.'}
                </h5>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validateOnChange={true}
                    validateOnBlur={true}
                    onSubmit={selectedRole ? handleUpdateRole : handleSubmit}
                    isInitialValid={false}
                >
                    {({ errors, isValid, values, setFieldValue, touched }) => (
                        <Form autoComplete='off' noValidate>
                            <div className='flex flex-col space-y-5'>
                                <Field
                                    as={Input}
                                    type='text'
                                    size='sm'
                                    isError={touched?.name && errors?.name}
                                    label='Role name'
                                    error={touched?.name && errors?.name}
                                    id='name'
                                    name='name'
                                    placeholder='Role Name'
                                />
                                <Field
                                    as={TextArea}
                                    rows='5'
                                    isError={
                                        touched?.description &&
                                        errors?.description
                                    }
                                    label='Role description'
                                    error={
                                        touched?.description &&
                                        errors?.description
                                    }
                                    id='description'
                                    name='description'
                                    placeholder='Description'
                                />

                                <div className='space-y-1'>
                                    <h3 className='text-ebony text-base font-medium'>
                                        Set Permissions
                                    </h3>
                                    <PermissionsView
                                        actionPermissions={
                                            values?.permissions || {}
                                        }
                                        updatePermission={(updated) =>
                                            setFieldValue(
                                                'permissions',
                                                updated
                                            )
                                        }
                                        isCustom={true}
                                    />
                                </div>
                                <div className=' w-full pb-10 gap-4 flex items-center'>
                                    <Button
                                        text={
                                            selectedRole ? 'Update' : 'Add role'
                                        }
                                        type='submit'
                                        isLoading={submitting}
                                        className='w-full'
                                        size='sm'
                                        disabled={!isValid || submitting}
                                    />
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </Drawer>
    );
};

export default CreateRoleModal;
